import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { Calendar, Clock, ArrowRight, CalendarDays, Info, Ticket, Users, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // "list" or "calendar"
  const [bookingEvent, setBookingEvent] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    name: "",
    email: "",
    phone: "",
    adult_tickets: 0,
    child_tickets: 0
  });
  const [submitting, setSubmitting] = useState(false);

  useSEO({
    title: "Events Calendar | The Elizabethan Centre, Whaplode Drove",
    description: "Discover upcoming events at The Elizabethan Centre - live music, tribute acts, quizzes, Sunday lunches, curry nights and more. Book tickets online for events in Whaplode Drove, Lincolnshire.",
    canonical: "https://www.elizabethancentre.co.uk/events"
  });

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events?upcoming_only=true`);
        setEvents(response.data);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  // Get dates with events for calendar highlighting
  const eventDates = events.map(event => new Date(event.event_date));

  // Filter events for selected date
  const filteredEvents = selectedDate 
    ? events.filter(event => {
        const eventDate = new Date(event.event_date);
        return eventDate.toDateString() === selectedDate.toDateString();
      })
    : events;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'long',
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const openBookingDialog = (event) => {
    setBookingEvent(event);
    setBookingForm({
      name: "",
      email: "",
      phone: "",
      adult_tickets: 1,
      child_tickets: 0
    });
  };

  const closeBookingDialog = () => {
    setBookingEvent(null);
    setBookingForm({
      name: "",
      email: "",
      phone: "",
      adult_tickets: 0,
      child_tickets: 0
    });
  };

  const calculateTotal = () => {
    if (!bookingEvent) return 0;
    const adultPrice = bookingEvent.ticket_price_adult || 0;
    const childPrice = bookingEvent.ticket_price_child || 0;
    return (bookingForm.adult_tickets * adultPrice) + (bookingForm.child_tickets * childPrice);
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    if (bookingForm.adult_tickets === 0 && bookingForm.child_tickets === 0) {
      toast.error("Please select at least one ticket");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/ticket-bookings`, {
        event_id: bookingEvent.id,
        name: bookingForm.name,
        email: bookingForm.email,
        phone: bookingForm.phone || null,
        adult_tickets: bookingForm.adult_tickets,
        child_tickets: bookingForm.child_tickets
      });
      
      const successMessage = bookingEvent.is_free_event 
        ? "Reservation confirmed! You will receive a confirmation email shortly."
        : "Tickets booked successfully! You will receive a confirmation email shortly.";
      toast.success(successMessage);
      closeBookingDialog();
    } catch (error) {
      console.error("Booking error:", error);
      const message = error.response?.data?.detail || "Failed to complete booking. Please try again.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const hasTicketing = (event) => {
    // Show booking button if event has ticket prices OR is a free event with booking enabled
    const hasPaidTickets = event.ticket_price_adult !== null && event.ticket_price_adult !== undefined && event.ticket_price_adult > 0;
    const hasFreeBooking = event.is_free_event && event.allow_free_booking;
    return hasPaidTickets || hasFreeBooking;
  };

  const isFreeEvent = (event) => {
    return event.is_free_event === true;
  };

  return (
    <div data-testid="events-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#F5F5F0]" data-testid="events-hero">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
            <div>
              <span className="inline-block bg-[#14532D]/10 text-[#14532D] text-sm font-medium px-4 py-1.5 rounded-full mb-6">
                What's On
              </span>
              <h1 className="heading-xl mb-4">Events Calendar</h1>
              <p className="text-xl text-[#292524]">
                Discover upcoming events at The Elizabethan Centre
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant={viewMode === "list" ? "default" : "outline"}
                onClick={() => setViewMode("list")}
                className={viewMode === "list" ? "btn-primary" : "btn-outline"}
                data-testid="list-view-btn"
              >
                List View
              </Button>
              <Button
                variant={viewMode === "calendar" ? "default" : "outline"}
                onClick={() => setViewMode("calendar")}
                className={viewMode === "calendar" ? "btn-primary" : "btn-outline"}
                data-testid="calendar-view-btn"
              >
                Calendar View
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding" data-testid="events-content">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="spinner" />
            </div>
          ) : viewMode === "calendar" ? (
            /* Calendar View */
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1">
                <Card className="card-heritage sticky top-24">
                  <CardContent className="p-6">
                    <h3 className="font-semibold font-serif text-lg mb-4 flex items-center gap-2">
                      <CalendarDays className="w-5 h-5 text-[#14532D]" />
                      Select a Date
                    </h3>
                    <CalendarComponent
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      modifiers={{
                        hasEvent: eventDates
                      }}
                      modifiersStyles={{
                        hasEvent: {
                          backgroundColor: '#DCFCE7',
                          color: '#15803D',
                          fontWeight: 'bold'
                        }
                      }}
                      className="rounded-md border"
                      data-testid="events-calendar"
                    />
                    {selectedDate && (
                      <Button 
                        variant="ghost" 
                        onClick={() => setSelectedDate(null)}
                        className="w-full mt-4 text-sm"
                      >
                        Clear Selection
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className="lg:col-span-2">
                {selectedDate && (
                  <div className="mb-6 p-4 bg-[#F5F5F0] rounded-lg flex items-center gap-3">
                    <Info className="w-5 h-5 text-[#14532D]" />
                    <span className="text-sm">
                      Showing events for: <strong>{formatDate(selectedDate)}</strong>
                    </span>
                  </div>
                )}
                
                {filteredEvents.length === 0 ? (
                  <Card className="card-heritage">
                    <CardContent className="p-12 text-center">
                      <Calendar className="w-16 h-16 text-[#D6D3D1] mx-auto mb-4" />
                      <h3 className="text-xl font-semibold font-serif mb-2 text-[#1C1917]">
                        {selectedDate ? "No Events on This Date" : "No Upcoming Events"}
                      </h3>
                      <p className="text-subtle mb-6">
                        {selectedDate 
                          ? "Try selecting a different date or clear your selection."
                          : "Check back soon for upcoming community events."}
                      </p>
                      <Link to="/book">
                        <Button className="btn-primary">
                          Book the Hall for Your Event
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-6">
                    {filteredEvents.map((event) => (
                      <EventCard 
                        key={event.id} 
                        event={event} 
                        formatDate={formatDate} 
                        onBookTickets={openBookingDialog}
                        hasTicketing={hasTicketing}
                        isFreeEvent={isFreeEvent}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* List View */
            <>
              {events.length === 0 ? (
                <Card className="card-heritage max-w-2xl mx-auto">
                  <CardContent className="p-12 text-center">
                    <Calendar className="w-16 h-16 text-[#D6D3D1] mx-auto mb-4" />
                    <h3 className="text-xl font-semibold font-serif mb-2 text-[#1C1917]">
                      No Upcoming Events
                    </h3>
                    <p className="text-subtle mb-6">
                      Check back soon for upcoming community events, or book the hall for your own event!
                    </p>
                    <Link to="/book">
                      <Button className="btn-primary">
                        Book the Hall
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {events.map((event) => (
                    <EventCard 
                      key={event.id} 
                      event={event} 
                      formatDate={formatDate} 
                      compact 
                      onBookTickets={openBookingDialog}
                      hasTicketing={hasTicketing}
                      isFreeEvent={isFreeEvent}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#F5F5F0]" data-testid="events-cta">
        <div className="container-custom text-center">
          <h2 className="heading-lg mb-4">Host Your Own Event</h2>
          <p className="text-body max-w-2xl mx-auto mb-8">
            The Elizabethan Centre is available for hire. Book the hall for your own celebration, 
            meeting, or community event.
          </p>
          <Link to="/book">
            <Button className="btn-primary">
              Book the Hall
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Ticket Booking Dialog */}
      <Dialog open={!!bookingEvent} onOpenChange={(open) => !open && closeBookingDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2">
              <Ticket className="w-5 h-5 text-[#14532D]" />
              {bookingEvent?.is_free_event ? "Reserve Your Spot" : "Book Tickets"}
            </DialogTitle>
            <DialogDescription>
              {bookingEvent?.title} - {bookingEvent?.event_date && formatDate(bookingEvent.event_date)}
            </DialogDescription>
          </DialogHeader>
          
          {bookingEvent && (
            <form onSubmit={handleBookingSubmit} className="space-y-4">
              {/* Ticket Prices Info - different for free vs paid events */}
              {bookingEvent.is_free_event ? (
                <div className="bg-[#DCFCE7] rounded-lg p-4 text-center">
                  <p className="font-semibold text-[#15803D]">Free Event</p>
                  <p className="text-sm text-[#166534]">Reserve your spot - no payment required</p>
                </div>
              ) : (
                <div className="bg-[#F5F5F0] rounded-lg p-4">
                  <h4 className="font-semibold text-sm mb-2">Ticket Prices</h4>
                  <div className="flex justify-between text-sm">
                    <span>Adult Ticket</span>
                    <span className="font-mono">£{bookingEvent.ticket_price_adult?.toFixed(2) || "0.00"}</span>
                  </div>
                  {!bookingEvent.adults_only && (
                    <div className="flex justify-between text-sm mt-1">
                      <span>Child Ticket</span>
                      <span className="font-mono">£{bookingEvent.ticket_price_child?.toFixed(2) || "0.00"}</span>
                    </div>
                  )}
                  {bookingEvent.adults_only && (
                    <p className="text-xs text-[#B91C1C] mt-2">This is an adults-only event</p>
                  )}
                </div>
              )}

              {/* Contact Details */}
              <div>
                <Label className="label-heritage">Your Name *</Label>
                <Input
                  value={bookingForm.name}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, name: e.target.value }))}
                  className="input-heritage"
                  required
                  data-testid="ticket-name-input"
                />
              </div>
              <div>
                <Label className="label-heritage">Email Address *</Label>
                <Input
                  type="email"
                  value={bookingForm.email}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, email: e.target.value }))}
                  className="input-heritage"
                  required
                  data-testid="ticket-email-input"
                />
              </div>
              <div>
                <Label className="label-heritage">Phone Number</Label>
                <Input
                  type="tel"
                  value={bookingForm.phone}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="input-heritage"
                  data-testid="ticket-phone-input"
                />
              </div>

              {/* Ticket Selection */}
              <div className="space-y-3">
                <Label className="label-heritage">{bookingEvent.is_free_event ? "Number of People" : "Number of Tickets"}</Label>
                
                {/* Adult Tickets */}
                <div className="flex items-center justify-between bg-[#F5F5F0] rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#14532D]" />
                    <span className="text-sm">Adult</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setBookingForm(prev => ({ ...prev, adult_tickets: Math.max(0, prev.adult_tickets - 1) }))}
                      disabled={bookingForm.adult_tickets === 0}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="w-8 text-center font-mono" data-testid="adult-ticket-count">{bookingForm.adult_tickets}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setBookingForm(prev => ({ ...prev, adult_tickets: prev.adult_tickets + 1 }))}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Child Tickets - only show if not adults-only */}
                {!bookingEvent.adults_only && (
                  <div className="flex items-center justify-between bg-[#F5F5F0] rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#78716C]" />
                      <span className="text-sm">Child</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setBookingForm(prev => ({ ...prev, child_tickets: Math.max(0, prev.child_tickets - 1) }))}
                        disabled={bookingForm.child_tickets === 0}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-8 text-center font-mono" data-testid="child-ticket-count">{bookingForm.child_tickets}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setBookingForm(prev => ({ ...prev, child_tickets: prev.child_tickets + 1 }))}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Total - hide for free events */}
              {!bookingEvent.is_free_event && (
                <div className="border-t border-[#E7E5E4] pt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total</span>
                    <span className="text-2xl font-bold font-mono text-[#14532D]" data-testid="ticket-total">
                      £{calculateTotal().toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button 
                  type="submit" 
                  className="btn-primary w-full" 
                  disabled={submitting || (bookingForm.adult_tickets === 0 && bookingForm.child_tickets === 0)}
                  data-testid="submit-ticket-booking"
                >
                  {submitting 
                    ? (bookingEvent.is_free_event ? "Reserving..." : "Booking...") 
                    : (bookingEvent.is_free_event ? "Reserve Spot" : "Book Tickets")
                  }
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Event Card Component
function EventCard({ event, formatDate, compact = false, onBookTickets, hasTicketing, isFreeEvent }) {
  const [imageError, setImageError] = useState(false);
  
  const imageUrl = event.image_url 
    ? (event.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${event.image_url}` : event.image_url)
    : null;

  const showTicketButton = hasTicketing(event);

  return (
    <Card 
      className={`event-card ${compact ? '' : 'animate-fade-in'} overflow-hidden`} 
      data-testid={`event-card-${event.id}`}
    >
      {/* Event Image - Full image display */}
      {imageUrl && !imageError && (
        <div className="w-full bg-[#F5F5F0] flex items-center justify-center p-4">
          <img 
            src={imageUrl} 
            alt={event.title}
            className="max-w-full max-h-64 object-contain rounded-lg"
            onError={() => setImageError(true)}
          />
        </div>
      )}
      <CardContent className={compact ? "p-6" : "p-8"}>
        <div className={`flex ${compact ? 'flex-col' : 'items-start gap-6'}`}>
          <div className={`bg-[#14532D] text-white rounded-lg p-4 text-center ${compact ? 'mb-4' : 'min-w-[80px]'}`}>
            <span className="block text-3xl font-bold font-mono">
              {new Date(event.event_date).getDate()}
            </span>
            <span className="block text-sm uppercase">
              {new Date(event.event_date).toLocaleDateString('en-GB', { month: 'short' })}
            </span>
            <span className="block text-xs mt-1 opacity-80">
              {new Date(event.event_date).getFullYear()}
            </span>
          </div>
          <div className="flex-1">
            <h3 className="font-semibold font-serif text-xl mb-2 text-[#1C1917]">
              {event.title}
            </h3>
            <p className="text-subtle flex items-center gap-2 mb-3">
              <Clock size={16} />
              {event.start_time} - {event.end_time}
            </p>
            <p className="text-body mb-4">
              {event.description}
            </p>
            
            {/* Ticket Price Display / Free Event Booking */}
            {showTicketButton && (
              <div className="flex items-center justify-between flex-wrap gap-3 mt-4 pt-4 border-t border-[#E7E5E4]">
                {isFreeEvent(event) ? (
                  <>
                    <div className="text-sm">
                      <span className="font-semibold text-[#15803D] bg-[#DCFCE7] px-2 py-1 rounded">
                        Free Entry
                      </span>
                      {event.adults_only && (
                        <span className="ml-2 text-xs bg-[#78350F]/10 text-[#78350F] px-2 py-0.5 rounded">18+</span>
                      )}
                    </div>
                    <Button 
                      className="btn-primary"
                      onClick={() => onBookTickets(event)}
                      data-testid={`book-tickets-${event.id}`}
                    >
                      <Ticket size={16} className="mr-2" />
                      Reserve Spot
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="text-sm">
                      <span className="text-[#78716C]">From </span>
                      <span className="font-semibold text-[#14532D]">
                        £{event.ticket_price_adult?.toFixed(2)}
                      </span>
                      <span className="text-[#78716C]"> per adult</span>
                      {event.adults_only && (
                        <span className="ml-2 text-xs bg-[#78350F]/10 text-[#78350F] px-2 py-0.5 rounded">18+</span>
                      )}
                    </div>
                    <Button 
                      className="btn-primary"
                      onClick={() => onBookTickets(event)}
                      data-testid={`book-tickets-${event.id}`}
                    >
                      <Ticket size={16} className="mr-2" />
                      Book Tickets
                    </Button>
                  </>
                )}
              </div>
            )}
            
            {!compact && !showTicketButton && (
              <p className="text-subtle mt-4 text-sm">
                <Calendar size={14} className="inline mr-1" />
                {formatDate(event.event_date)}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
